import { observer } from "mobx-react";
import { Col, Row } from "react-bootstrap";
import * as React from "react";
import { RootStoreContext } from "../../../stores/RootStore";
import { useContext, useEffect, useMemo, useState } from "react";
import { costString, dualCostString } from "../../../config/Registration";
import "./EventSelection.css";

// Default event image URL - using a placeholder image that represents events/entertainment
const DEFAULT_EVENT_IMAGE = `${process.env.PUBLIC_URL || ''}/01.png`;

const EventSelection = observer(({ events, onEventSelect }) => {
  const { eventPhotoStore, registrationStore } = useContext(RootStoreContext);
  const { formatEventDate } = registrationStore;
  const recommendedEvents = useMemo(
    () => events.filter(event => event.recommendedForUser),
    [events]
  );
  const [filter, setFilter] = useState<'recommended' | 'all'>(
    recommendedEvents.length > 0 ? 'recommended' : 'all'
  );

  useEffect(() => {
    if (recommendedEvents.length === 0 && filter === 'recommended') {
      setFilter('all');
    }
  }, [filter, recommendedEvents.length]);

  const visibleEvents = filter === 'recommended' ? recommendedEvents : events;

  useEffect(() => {
    // Load photos for all events when component mounts
    events.forEach(event => {
      eventPhotoStore.loadEventPhotoQuietly(event.eventId);
    });

  }, [events]);

  const renderEventPrice = (event) => {
    if (!event.tickets?.length) return null;
    const minPrice = Math.min(...event.tickets.map(ticket => ticket.effectiveCoinsPrice));
    const maxPrice = Math.max(...event.tickets.map(ticket => ticket.effectiveCoinsPrice));
      
    if (minPrice === maxPrice) {
      // All tickets cost the same effectively. Only show the strikethrough promo
      // when the underlying original/promo pricing is also uniform; otherwise the
      // first ticket's raw prices would misrepresent the other tickets.
      const firstTicket = event.tickets[0];
      const uniformPricing = event.tickets.every(
        (t) => t.coinsPrice === firstTicket.coinsPrice && t.promoCoinsPrice === firstTicket.promoCoinsPrice
      );
      return uniformPricing
        ? <p>{dualCostString(firstTicket.coinsPrice, firstTicket.promoCoinsPrice)}</p>
        : <p>{costString(minPrice)}</p>;
    } else {
      return <p>Ȼ{minPrice} - Ȼ{maxPrice}</p>;
    }
  };

  const hasAnyPurchasedTicket = (event) => {
    return event.purchasedTickets && event.purchasedTickets.length > 0;
  };



    return (
    <div className="container-fluid" style={{ maxWidth: '900px'}}>
      <div className="events-overview-filter" role="group" aria-label="Filter events">
        <button
          type="button"
          className={filter === 'recommended' ? 'active' : ''}
          aria-pressed={filter === 'recommended'}
          disabled={recommendedEvents.length === 0}
          onClick={() => setFilter('recommended')}
        >
          Recommended for{' '}you
        </button>
        <button
          type="button"
          className={filter === 'all' ? 'active' : ''}
          aria-pressed={filter === 'all'}
          onClick={() => setFilter('all')}
        >
          All events
        </button>
      </div>
      <Row className="g-4">
        {visibleEvents.map((event, index) => {
          const eventPhoto = eventPhotoStore.getEventPhoto(event.eventId);
          const hasPurchasedTicket = hasAnyPurchasedTicket(event);
          return (
            <Col key={`event-${index}`} xs={12} md={6}>
              <button
                type="button"
                className={`event-card ${event.selected ? 'selected' : ''} ${hasPurchasedTicket ? 'purchased' : ''}`}
                onClick={() =>  onEventSelect(event)}
                style={{
                  cursor: 'pointer',
                  border: event.selected ? '1px solid #013220' : '1px solid transparent',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: event.selected ? '0 0 0 2px rgba(135, 201, 211, 0.25), 0 2px 8px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s',
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transform: event.selected ? 'scale(1.02)' : 'scale(1)',
                  fontSize: '18px',
                  width: '100%',
                  padding: 0,
                  background: 'white',
                  color: 'inherit',
                  textAlign: 'left',
                }}
              >
                <div style={{ position: 'relative' }}>
                  <div style={{ 
                    width: '100%', 
                    paddingTop: '100%',
                    position: 'relative' 
                  }}>
                    <img
                      src={(eventPhoto && eventPhoto.photoUrl) ? eventPhoto.photoUrl : DEFAULT_EVENT_IMAGE}
                      alt={event.title}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                </div>
                <div className="p-3" style={{ 
                  flex: '0 0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  backgroundColor: event.selected ? '#edf7f9' : undefined
                }}>
                  <strong className="text-muted mb-1">
                    {formatEventDate(event.startTime, event.endTime)}
                  </strong>
                  <p className="mb-1">{event.title}</p>
                  {renderEventPrice(event)}
                </div>
              </button>
            </Col>
          );
        })}
      </Row>
    </div>
  );
});

export default EventSelection; 
